/* eslint-disable no-nested-ternary */
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBinoculars,
  faCircle,
  faPencil,
  faTrash,
  faInfo,
  faReceipt,
  faEye,
  faArrowRightFromBracket,
  faPaperPlane, faPaperclip, faQuestionCircle,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import { MODULE_FIELD_TYPES, OVERRIDE_FIELDS } from '../constants'
import { selectDropdowns, selectDropdownOptions } from '../redux/dropdownSlice'
import { generateTableKey } from '../utils/listViewHelper'
import SuccessIcon from './elements/SuccessIcon'
import DeclineIcon from './elements/DeclineIcon'
import { validURL } from "../helpers/formValidation"
import { hasUserPermission } from '../utils/permissionHelper'
import { selectUser } from '../redux/userSlice'
import { getActivePermissions } from '../redux/permissionSelectors'
import { setModal } from "../redux/modalSlice"
import { selectMultiSelectedRows, setSelectedRows } from '../redux/multiSelectRowSlice'
import DownloadRcRecordingButton from "./DownloadRcRecordingButton";
import PaymentScheduleOptions from './PaymentScheduleOptions'
import { PercentFormat, CurrencyFormat, DateFormat, isDateAfterDays } from '../helpers/formatting'
import ManagerApprovalIcons from './elements/ManagerApprovalIcons'
import WireApprovalButtons from "./WireApprovalButtons";
import ViewSensitivePiiButtons from "./ViewSensitivePiiButtons";
import { handleToolTipMouseEnter } from '../helpers/layout'
import OverrideTag from './elements/OverrideTag'
import { NotificationService } from "../services/NotificationService";
import TableRowTextShowMore from './elements/TableRowTextShowMore'
import { setNotification } from '../redux/notificationSlice'
import { useRequest } from '../hooks/UseRequest'

const getRoleBasedRouting = (active, dropdownOptions, baseName, row, isSubpanel, subBaseName) => {
  if (baseName == 'customer') {
    const customerPage = dropdownOptions.find((x) => x?.id === active?.data?.customer_page_dropdown_option_id)?.code_key
    if (customerPage == 'customer_page_detail_view') {
      baseName = 'customer'
    } else if (customerPage == 'customer_page_verifications') {
      baseName = 'manual_verification'
    } else if (customerPage == 'customer_page_payment_schedule') {
      baseName = 'payment_schedule'
    }
  }

  let route = `/${baseName}/${row.id}`;
  if (isSubpanel) {
    if (baseName == "payment_schedule") {
      route = `/payment/${row.id}`
    } else if (subBaseName !== null) {
      route = `/${subBaseName}/${row.id}`
    }
  }

  return route;
}

const handleToolTipMouseLeave = (event) => {
  const tooltipDiv = event.currentTarget.querySelector('.payment-schedule-tooltip');
  if (tooltipDiv) {
    tooltipDiv.remove();
  }
  const thFixed = event.target.closest('th.fixed-sticky')
  if (thFixed) {
    thFixed.classList.add('fixed')
    thFixed.classList.add('sticky')
    thFixed.classList.remove('fixed-sticky')
  }
};

const getToolTipInfo = (type, columns, isDetailView) => {
  if (type === 'nsf_fee') {
    return `NSF Fee: ${columns['nsf_fee'] ?? 0}\nAssessed NSF Fee: ${columns['assessed_nsf_fee'] ?? 0}\nPre-Discount NSF Fee: ${columns['prediscount_nsf_fee'] ?? 0}${isDetailView ? `\nActual NSF Fee Amount: ${columns[`actual_nsf_fee_amount`] ?? 0}` : ''}`;
  }
  if (type === 'late_fee') {
    return `Late Fee: ${columns['late_fee'] ?? 0}\nAssessed Late Fee: ${columns['assessed_late_fee'] ?? 0}\nPre-Discount Late Fee: ${columns['prediscount_late_fee'] ?? 0}${isDetailView ? `\nActual Late Fee Amount: ${columns[`actual_late_fee_amount`] ?? 0}` : ''}`;
  }
  return '';
};

export function IconWithHelperText(props) {
  const { icon, helperText, onClick = null, hasMultipleLine = false } = props
  return (
    <span
      style={{ pointerEvents: 'auto' }}
      className={`lox-no-border lox-tooltip-moveable lox-tooltip-icon-helper has-tooltip-arrow ${hasMultipleLine ? 'has-tooltip-multiline' : ''} has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
      data-tooltip={helperText}
      onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
      onMouseLeave={handleToolTipMouseLeave}
    >
      <FontAwesomeIcon icon={icon} className={`has-text-white`} onClick={onClick} />
    </span>
  )
}
/**
 * Render each table cell based on the field type
 * @param header
 * @param columns
 * @param rowId
 * @param dropdowns
 * @param dropdownOptions
 * @param active
 * @param baseName
 * @param parentModuleCodeKey
 * @param isDetailView
 * @returns {JSX.Element}
 */
function valueColumn(header, columns, rowId, dropdowns, dropdownOptions, active, baseName, parentModuleCodeKey, isDetailView) {
  const activePermissions = getActivePermissions(active)

  if (!hasUserPermission(activePermissions, 'show_ids') && header.column_name === 'id') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>
  }
  let alignText = 'has-text-left';
  let cellHighlight = (baseName === "payment_schedule" && header.column_name === 'payment_type_dropdown_option_id' && columns[`payment_type_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_type_deferred')?.id) ? 'payment-schedule-background-purple' : '';
  const fieldType = dropdownOptions[header.module_field_type_dropdown_option_id].code_key;
  let cellData = columns[header.column_name]
  if (cellData || (!cellData && fieldType === MODULE_FIELD_TYPES.BOOLEAN.code_key)) {
    switch (fieldType) {
      case MODULE_FIELD_TYPES.BOOLEAN.code_key:
        if (cellData === 1) {
          alignText = 'has-text-centered';
          cellData = (
            <SuccessIcon />
          )
        }
        if (cellData === 0) {
          alignText = 'has-text-centered';
          cellData = (
            <DeclineIcon />
          )
        }
        break

      case MODULE_FIELD_TYPES.DATE.code_key:
        if (cellData) {
          cellData = <DateFormat dateString={cellData} />
        }
        break
      case MODULE_FIELD_TYPES.DATETIME.code_key:
        if (cellData) {
          cellData = <DateFormat dateString={cellData} formatString="MM/dd/yyyy hh:mm:ss a" />
        }
        break

      case MODULE_FIELD_TYPES.CURRENCY.code_key:
        if (cellData) {
          cellData = <CurrencyFormat value={cellData} showCurrencySign={false} />
        }
        break

      case MODULE_FIELD_TYPES.PERCENT.code_key:
        if (cellData) {
          if (header.column_name === "apr") {
            cellData = <PercentFormat value={cellData} percentSign={false} fractionDigits={3} />
          } else {
            cellData = <PercentFormat value={cellData} percentSign={false} />
          }
        }
        break

      case MODULE_FIELD_TYPES.DROPDOWN_SYSTEM.code_key:
        const dropdown_option = dropdowns.data.find((x) => x.code_key === header.dropdown_key).dropdown_options.find((x) => x.id == cellData)
        cellData = (dropdown_option && dropdown_option.name) ? dropdown_option.name : '-'
        if (header.dropdown_key == "mgr_approval_status") {
          cellData = <><ManagerApprovalIcons type={dropdown_option.code_key} /><span className="pl-2">{cellData}</span></>
        }
        break

      case MODULE_FIELD_TYPES.DROPDOWN_SEARCHABLE.code_key:
      case MODULE_FIELD_TYPES.DROPDOWN.code_key:
        const field_label = header.column_name.replace('_id', '_label')
        let field_value = columns[field_label]
        if (!field_value && parentModuleCodeKey === 'module_queue_general') {
          field_value = columns[header.column_name]
        }
        const data = header.module_code_key === 'dropdown_option' ? dropdowns.data.find((e) => e.id === cellData).name : field_value
        cellData = (<a href={`/${header.dropdown_relation}/${columns[header.column_name]}`}>{data}</a>)
        break

      case MODULE_FIELD_TYPES.URL.code_key:
        if (validURL(cellData)) {
          cellData = <a href={cellData} target="_blank" rel="noreferrer">{cellData}</a>
        }
        break

      case MODULE_FIELD_TYPES.S3_LINK.code_key:
        if (validURL(cellData)) {
          cellData = (
            <a className="button is-primary" href={cellData} target="_blank" rel="noopener noreferrer">
              <IconWithHelperText icon={faEye} helperText={'View File'} />
            </a>
          )
        }
        break

      case MODULE_FIELD_TYPES.TEXT_AREA.code_key:
        cellData = (
          <div>
            <TableRowTextShowMore text={cellData} tableRow />
          </div>
        )
        break

      case MODULE_FIELD_TYPES.TAG_CLOUD.code_key:
        cellData = (!cellData || cellData.length === 0) ? '-' : (
          <span className="tags pt-1 pb-0">
            {cellData.map((item, index) => (
              <span className="tag is-link is-rounded" style={{ marginBottom: "0.4rem", padding: "0.5em", height: "1.2em", }} key={item.id}>{item.name}</span>
            ))}
          </span>
        )
        break

      case MODULE_FIELD_TYPES.ENCRYPTED.code_key:
        const last_four = `${header.column_name}_last_four`
        const max_length = header.max_length ? header.max_length : 10

        if (columns[last_four]) {
          cellData = '#'.repeat(max_length - 4) + columns[last_four]
        } else {
          cellData = '#'.repeat(max_length)
        }
        break

      default:
        break
    }
  } else {
    cellData = '-'
  }

  if (baseName === "payment_schedule" && (header.column_name === 'nsf_fee' || header.column_name === 'late_fee')) {
    const feeInfo = getToolTipInfo(header.column_name, columns, isDetailView);
    const assessedFee = columns[`assessed_${header.column_name}`] ?? 0;
    const preDiscountFee = columns[`prediscount_${header.column_name}`] ?? 0;
    const actualFee = columns[`actual_${header.column_name}_amount`] ?? 0;
    const isShowFeeTooltip = (assessedFee && assessedFee > 0) || (preDiscountFee && preDiscountFee > 0) || (actualFee != null && cellData !== '-' && actualFee !== cellData);

    cellData = (
      <>
        {cellData}
        <span
          className={`${isShowFeeTooltip ? 'lox-no-border' : 'is-hidden'
            } lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={feeInfo}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === "payment_schedule" && header.column_name === 'id' && isDetailView) {
    const tooltipContent = `Payment Plan Payment ID: ${columns?.[`payment_plan_payment`]?.['id']}`
    cellData = (
      <>
        {cellData}
        {columns[`payment_plan_payment`] && (
          <span
            className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal right'}
            data-tooltip={tooltipContent}
          >
            <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
          </span>
        )}
      </>
    );
  } else if (baseName === "payment_schedule" && header.column_name === 'payment_type_dropdown_option_id') {
    if (columns[`payment_type_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_type_paydown')?.id || columns[`payment_type_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_type_makeup')?.id || columns[`payment_type_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_type_credit')?.id || columns[`payment_type_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_type_payoff')?.id || columns[`payment_type_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_type_collection')?.id) {
      const overagePaymentId = columns[`overage_payment_id`] ?? null;
      const tooltipContent = `Overpayment from payment: ${overagePaymentId}`;
      cellData = (
        <>
          {cellData}
          <span
            data-testid={overagePaymentId ? `payment-row-overage-of-${overagePaymentId}` : undefined}
            className={`${overagePaymentId ? 'lox-no-border' : 'is-hidden'} lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
            data-tooltip={tooltipContent}
          >
            <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
          </span>
        </>
      );
    } else if (columns[`payment_type_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_type_deferred')?.id) {
      const deferredPaymentId = columns[`deferred_payment_id`] ?? null;
      const tooltipContent = `Deferred payment: ${deferredPaymentId}`;
      cellData = (
        <>
          {cellData}
          <span
            className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
            data-tooltip={tooltipContent}
          >
            <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
          </span>
        </>
      );
    } else {
      const rescheduledPaymentDate = columns[`rescheduled_payment_date`] ?? null;
      const rescheduledPaymentId = columns[`rescheduled_payment_id`] ?? null;
      const tooltipContent = `#${rescheduledPaymentId} - ${rescheduledPaymentDate}`;
      cellData = (
        <>
          {cellData}
          <span
            className={`${(rescheduledPaymentDate !== null && rescheduledPaymentId != null) ? 'lox-no-border' : 'is-hidden'
              } lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
            data-tooltip={tooltipContent}
          >
            <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
          </span>
        </>
      );
    }
  } else if (baseName === "payment_schedule" && header.column_name === 'principal_amount') {
    const prediscountPrincipal = columns[`prediscount_principal`] ?? 0;
    const tooltipContent = `Pre-Discount Principal: ${columns[`prediscount_principal`]}${isDetailView ? `\nActual Amount: ${columns[`actual_principal_amount`] ?? 0}` : ''}`;
    const isShowPrincipalTooltip =
      (+columns[`principal_amount`] != prediscountPrincipal) ||
      (isDetailView && columns[`actual_principal_amount`] != null && +columns[`principal_amount`] !== columns[`actual_principal_amount`]);
    const adjustmentTooltipContent = 'This payment has been adjusted by the system and the principal/interest distribution for this payment is different from the original you see here.';

    cellData = (
      <>
        {cellData}
        <span
          className={`${isShowPrincipalTooltip ? 'lox-no-border' : 'is-hidden'} lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={tooltipContent}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>

        <span
          className={`${columns['has_adjustment_payment'] ? 'lox-no-border' : 'is-hidden'} lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={adjustmentTooltipContent}
        >
          <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === "payment_schedule" && header.column_name === 'interest_amount') {
    const prediscountInterest = columns[`prediscount_interest`] ?? 0;
    const tooltipContent = `Pre-Discount Interest: ${columns[`prediscount_interest`]}${isDetailView ? `\nActual Interest Amount: ${columns[`actual_interest_amount`] ?? 0}` : ''}`;
    const isShowInterestTooltip =
      (+columns[`interest_amount`] !== prediscountInterest) ||
      (isDetailView && columns[`actual_interest_amount`] != null && +columns[`interest_amount`] !== columns[`actual_interest_amount`]);
    cellData = (
      <>
        {cellData}
        <span
          className={`${isShowInterestTooltip ? 'lox-no-border' : 'is-hidden'} lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={tooltipContent}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === "payment_schedule" && header.column_name === 'scheduled_amount' && columns[`payment_status_dropdown_option_id`] == dropdownOptions.find((x) => x?.code_key === 'payment_status_paid')?.id && columns['actual_including_overage_amount'] != undefined) {
    // If the actual amount is different than the scheduled amount, show the icon with the actual amount paid.
    const actualAmount = columns['actual_including_overage_amount'];
    const tooltipContent = ` Actual Amount: ${actualAmount}`;
    cellData = (
      <>
        {cellData}
        <span
          className={`${tooltipContent != '' ? 'lox-no-border' : 'is-hidden'} lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={tooltipContent}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === "payment_schedule" && header.column_name === 'payment_status_dropdown_option_id') {
    let tooltipContent = columns['payment_status_detail'] ?? null;
    cellData = (
      <>
        {cellData}
        <span
          className={`${tooltipContent ? 'lox-no-border' : 'is-hidden'} lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={tooltipContent}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === "payment_schedule" && header.column_name === 'payment_method_dropdown_option_id') {
    const paymentMethodWireTransferDDo = dropdownOptions.find((x) => x?.code_key === 'payment_method_wire_transfer');
    const paymentPersonalCheckDdo = dropdownOptions.find((x) => x?.code_key === 'payment_method_personal_check');
    let tooltipContent = '';
    if (paymentMethodWireTransferDDo.id == columns[`payment_method_dropdown_option_id`] && columns[`wire_tracking_number`] != null) {
      tooltipContent = "Wire Tracking #: " + columns[`wire_tracking_number`]
    } else if (paymentPersonalCheckDdo.id == columns[`payment_method_dropdown_option_id`] && columns[`check_number`] != null) {
      tooltipContent = "Check #: " + columns[`check_number`]
    }

    cellData = (
      <>
        {cellData}
        <span
          className={`${tooltipContent != '' ? 'lox-no-border' : 'is-hidden'} lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={tooltipContent}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === "payment_schedule" && header.column_name === 'scheduled_date' && columns[`scheduled_date`] != columns[`original_scheduled_date`]) {
    const tooltipContent = `Original Date: ${DateFormat({ dateString: columns[`original_scheduled_date`] })}`;
    cellData = (
      <>
        {cellData}
        <span
          className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={tooltipContent}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === 'document' && fieldType === MODULE_FIELD_TYPES.S3_LINK.code_key) {
    const isBankruptcyDocument = columns.document_type_dropdown_option_id &&
      dropdownOptions.some((o) => o.code_key.startsWith('document_type_bankruptcy') && o.id === columns.document_type_dropdown_option_id);
    const isLoanAgreement = columns.document_type_dropdown_option_id &&
      dropdownOptions.some((o) => o.code_key === 'document_type_loan_agreement' && o.id === columns.document_type_dropdown_option_id);
    const isBusinessLicense = columns.document_type_dropdown_option_id &&
      dropdownOptions.some((o) => o.code_key === 'document_type_business_license' && o.id === columns.document_type_dropdown_option_id);
    const documentType = isBankruptcyDocument ? 'bankruptcy_document' : isLoanAgreement ? 'loan_agreement' : isBusinessLicense ? 'business_license' : 'general_document';
    cellData = (
        <ViewSensitivePiiButtons customerId={columns.customer_id} documentId={columns.id} documentTypeId={columns.document_type_dropdown_option_id} type={documentType} style={{ height: '20px' }} />
      )
  } else if (baseName in OVERRIDE_FIELDS && header.column_name in OVERRIDE_FIELDS[baseName] && columns[OVERRIDE_FIELDS[baseName][header.column_name].override_field] !== null && columns[OVERRIDE_FIELDS[baseName][header.column_name].override_source_field] !== null) {
    cellData = <OverrideTag value={cellData} sourceCodeKeyId={columns[OVERRIDE_FIELDS[baseName][header.column_name].override_source_field]} />
  } else if (baseName === 'campaign_batch' && (header.column_name === 'promotion_id')) {
    cellData = (
      <Link key={`view-${columns.promotion_id}`} to={`/${columns.promotion_module}/${columns.promotion_id}`} className="is-primary">
        {columns.promotion_label}
      </Link>
    )
  } else if (baseName === 'user_permission_option' && header.column_name === 'cando' && columns['cando_tooltip'] !== '') {
    cellData = (
      <>
        {cellData}
        <span
          className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
          data-tooltip={columns['cando_tooltip']}
        >
          <FontAwesomeIcon icon={faInfo} className="pl-1 has-text-link" style={{ paddingBottom: '.15rem !important' }} />
        </span>
      </>
    );
  } else if (baseName === 'loan_dmc' && header.column_name === 'loan_id') {
    cellData = (
      <Link to={`/payment_schedule/${columns.customer_id}?loan=${columns.loan_label}`}>
        {columns.loan_label}
      </Link>
    )
  }

  return (
    <td key={generateTableKey(header.column_name, rowId)} style={{ whiteSpace: 'nowrap' }} className={`${alignText} ${cellHighlight}`}>
      {cellData}
    </td>
  )
}

/**
 * TableRows Needs to be flexible enough to work within multiple "Table" components
 * @param rows
 * @param format
 * @returns {*}
 * @constructor
 */
export default function TableRow({
  rows,
  columns,
  hasActions = true,
  hasEdit = true,
  hasDelete = true,
  hasView = true,
  hasMultiSelect = false,
  baseName,
  updatePaymentStatus = null,
  deletePayment = null,
  discountPayment = null,
  isMappingTable = false,
  isSubpanel = false,
  subBaseName = null,
  parentModuleId = null,
  undiscountPayment = null,
  undeferPayment = null,
  nextRegularPaymentId = 0,
  promotionRedeem = null,
  loanId = 0,
  refreshPaymentSchedule = null,
  customerLocked = false,
  recordChargeback = null,
  submitUnlock,
  revertReschedule = null,
  revertDeferr = null,
  customRowName = null,
  customRowData = null,
  navigate = null,
  parentModuleCodeKey = null,
  isDetailView = false
}) {
  const dispatch = useDispatch()
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const active = useSelector(selectUser)
  const multiSelectedRows = useSelector(selectMultiSelectedRows)
  const activePermissions = getActivePermissions(active)
  const { markNotificationAsViewed } = NotificationService()
  const processingStatusVerifyDDOId = dropdownOptions.find((x) => x?.code_key === 'processing_status_verify')?.id
  const requests = useRequest()

  const handleManagerApprovalNotificationClick = (recordId, notificationId = null) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'manager_approval_modal',
        options: { recordId: recordId }
      })
    )
    if (notificationId) {
      markNotificationAsViewed(notificationId)
    }
  }

  const handleResendEmail = (emailMessage, customerEmailAddress) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'customer_resend_email_modal',
        options: { emailMessage: emailMessage, customerEmailAddress: customerEmailAddress }
      })
    )
  }

  const handleViewAttachment = async (id) => {
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer_email_message/${id}/attachment`)
      const response = await requests.get()
      // If the operation was successful, execute the success callback and reset the form
      if (response.status === 200 || response.status === 201) {
        window.open(response.data.attachment_link, "_blank", "noreferrer");
      }
    } catch (error) {
      // If an exception occurs, reset the saving state and show an error notification
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const handleEmailRedactedDocument = (loanId) => {
    navigate('documentEmailSlider', loanId)
  }

  const handlePaymentEntryAssignmentClick = (record) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'payment_entry_modal',
        options: { record: record }
      })
    )
  }

  if (dropdowns.data.length > 0 && dropdownOptions) {
    const tableRow = rows?.map((row, index) => {
      const cols = columns.map((head) => valueColumn(head, row, row.id, dropdowns, dropdownOptions, active, baseName, parentModuleCodeKey, isDetailView))

      // This code sets the background color of table rows based on the payment status.
      // It checks the 'payment_status_dropdown_option_id' of each row and compares it with IDs in 'dropdownOptions'.
      // Depending on the status, it sets the row's background color to green, light red, or light yellow.
      let rowStyle = ''
      if (baseName === "payment_schedule") {
        if (row['trashed']) {
          rowStyle = 'payment-schedule-color-dark-gray'
        } else if (row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_paid').id || (row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_c_code_paid').id && row['correction_date'])) {
          rowStyle = 'payment-schedule-background-green'
        } else if (row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_returned').id
          || row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_error').id
          || row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_void').id
          || row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_chargeback').id
          || row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_missed').id) {
          rowStyle = 'payment-schedule-background-red'
        } else if (row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_deferred').id) {
          rowStyle = 'payment-schedule-background-purple'
        } else if (row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_in_queue').id
          || row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_in_process').id) {
          rowStyle = 'payment-schedule-background-yellow'
        } else if (row['scheduled_amount'] === '0.00' && row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_scheduled').id) {
          rowStyle = 'payment-schedule-background-dark-gray'
        } else if (row['payment_correction_code_dropdown_option_id'] && !row['correction_date']) {
          rowStyle = 'payment-schedule-background-dark-red'
        }
      } else if (baseName === 'payment_check_entry' && isSubpanel) {
        rowStyle = 'payment-check-entry'
      } else if (baseName === 'payment' && !isSubpanel && row.module_code_key === "wire_approval_queue") {
        if (row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_paid').id) {
          rowStyle = 'payment-schedule-background-green'
        } else if (row['payment_status_dropdown_option_id'] == dropdownOptions.find((x) => x?.code_key === 'payment_status_returned').id) {
          rowStyle = 'payment-schedule-background-red'
        }
      }
      let customRowComponent = null;

      if (baseName === 'payment_schedule' && customRowName === 'collections_date' && customRowData && customRowData.afterDate && new Date(customRowData.afterDate) >= new Date(row.scheduled_date) && new Date(customRowData.afterDate) < new Date(rows[index + 1]?.scheduled_date)) {
        customRowComponent = (
          <tr className="payment-schedule-background-orange">
            <td className="custom-table-row has-text-centered" colSpan="100%"><span
              className="has-text-centered"
            >Sent to Collections on {DateFormat({ dateString: customRowData.afterDate })}</span></td>
          </tr>
        )
      }

      return (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          <tr className={`${rowStyle}`}>
            {hasMultiSelect ? (
              <th className="fixed has-text-centered sticky">
                <input
                  type="checkbox"
                  checked={(multiSelectedRows && multiSelectedRows.isAll) || multiSelectedRows.selectedRows.includes(row.id) ? true : false}
                  onChange={(e) => {
                    if (e.target.checked) {
                      dispatch(setSelectedRows({
                        isAll: false,
                        selectedRows: [...multiSelectedRows.selectedRows, row.id],
                      }));
                    } else {
                      dispatch(setSelectedRows({
                        isAll: false,
                        selectedRows: multiSelectedRows.selectedRows.filter((id) => id !== row.id),
                      }));
                    }
                  }}

                />
              </th>
            ) : (
              ''
            )}
            {hasView ? (
              <th className={`fixed sticky ${rowStyle}`}>
                {/* TODO: Need to use notification type id instead of module code key. can create a new function in utils to get the notification type id based on module code key */}
                {baseName === 'notification' && row.module_code_key === "manager_approval" ? (
                  <button
                    key={`view-${row.id}`}
                    className="button is-primary"
                  >
                    <IconWithHelperText icon={faBinoculars} helperText={'Manager Approval'} onClick={() => handleManagerApprovalNotificationClick(row.record_id, row.id)} />
                    {row.is_viewed === 0 && (
                      <span className="has-text-danger-dark has-text-weight-bold" style={{ fontSize: '7px', position: 'absolute', bottom: '13px', left: '16px' }}>
                        <FontAwesomeIcon icon={faCircle} />
                      </span>
                    )}
                  </button>
                ) : baseName === 'manager_approval' ? (
                  <button
                    key={`view-${row.id}`}
                    className="button is-primary"
                  >
                    <IconWithHelperText icon={faBinoculars} helperText={'Manager Approval'} onClick={() => handleManagerApprovalNotificationClick(row.id)} />
                    {row.is_viewed === 0 && (
                      <span className="has-text-danger-dark has-text-weight-bold" style={{ fontSize: '7px', position: 'absolute', bottom: '13px', left: '16px' }}>
                        <FontAwesomeIcon icon={faCircle} />
                      </span>
                    )}
                  </button>
                ) : baseName === 'loan' ? (
                  <table style={{ border: 'none' }}>
                    <tbody>
                      <tr>
                        <td>
                          <Link
                            key={`view-${row.id}`}
                            to={getRoleBasedRouting(active, dropdownOptions, baseName, row, isSubpanel, subBaseName)}
                            className="button is-primary"
                          >
                            <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                          </Link>
                        </td>
                        <td>
                          <Link
                            key={`view-${row.id}`}
                            to={`/payment_schedule/${row.customer_id}?loan=${row.guid}`}
                            className="button is-primary"
                          >
                            <IconWithHelperText icon={faReceipt} helperText={'Payment Schedule'} />
                          </Link>
                        </td>
                        {isSubpanel ? (
                          <td>
                            <button
                              key={`view-${row.id}`}
                              onClick={() => handleEmailRedactedDocument(row.id)}
                              className="button is-primary"
                            >
                              <IconWithHelperText
                                hasMultipleLine
                                icon={faPaperPlane}
                                helperText={'Send a redacted version of the document to an email address'}
                              />
                            </button>
                          </td>
                        ) : (
                          ''
                        )}
                      </tr>
                    </tbody>
                  </table>
                ) : baseName === 'lead_withdrawn' ? (
                  <table style={{ border: 'none' }}>
                    <tbody>
                      <tr>
                        <td>
                          <Link
                            key={`view-${row.id}`}
                            to={`/lead/${row.id}`}
                            className="button is-primary"
                          >
                            <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : baseName === 'lead' && isSubpanel ? (
                  <table style={{ border: 'none' }}>
                    <tbody>
                      <tr>
                        <td>
                          <Link
                            key={`view-${row.id}`}
                            to={getRoleBasedRouting(active, dropdownOptions, baseName, row, isSubpanel, subBaseName)}
                            className="button is-primary"
                          >
                            <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                          </Link>
                        </td>
                        <td>
                          <Link
                            key={`view-${row.id}`}
                            to={`/manual_verification/${row.customer_id}?lead=${row.guid}`}
                            className="button is-primary"
                          >
                            <IconWithHelperText icon={faReceipt} helperText={'Manual Verification'} />
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : baseName === 'customer_email_message' && isSubpanel ? (
                  <table style={{ border: 'none' }}>
                    <tbody>
                      <tr>
                        <td>
                          <Link
                            key={`view-${row.id}`}
                            to={getRoleBasedRouting(active, dropdownOptions, baseName, row, isSubpanel, subBaseName)}
                            className="button is-primary"
                          >
                            <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                          </Link>
                        </td>
                        {hasUserPermission(activePermissions, 'can_resend_email') && (
                          <td>
                            <button
                              key={`view-${row.id}`}
                              onClick={() => handleResendEmail(row, customRowName === 'customerEmailAddress' ? customRowData : null)}
                              className="button is-primary"
                              disabled={!row.can_resend}
                            >
                              <IconWithHelperText hasMultipleLine icon={faPaperPlane} helperText={row.can_resend ? `Resend Email: Send exact same email html to the customer again, but will use whatever the current email for the customer is including overrides from POA, DMC, C&D, etc.` : row.can_resend_explanation} />
                            </button>
                          </td>
                        )}
                        <td>
                          <button
                            key={`view-${row.id}`}
                            data-hover="View Message"
                            className="button is-primary"
                            onClick={() => dispatch(setModal({ title: row.subject, content: row, type: 'html_email_body' }))}
                            style={{
                              height: '1.5rem'
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                        </td>
                        <td>
                          <button
                            data-hover="View Attachment"
                            className="button is-primary"
                            onClick={() => handleViewAttachment(row.id)}
                            disabled={!row.attachment_name}
                            style={{
                              height: '1.5rem'
                            }}
                          >
                            <FontAwesomeIcon icon={faPaperclip} />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : baseName === 'notification' && row.module_code_key !== "manager_approval" ? (
                  <Link
                    key={`view-${row.id}`}
                    to={row.module_code_key && row.record_id ? `/${row.module_code_key}/${row.record_id}` : '/report'}
                    className="button is-primary"
                    state={{ notification_id: row.is_viewed === 0 ? row.id : null }}
                  >
                    <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                    {row.is_viewed === 0 && (
                      <span className="has-text-danger-dark has-text-weight-bold" style={{ fontSize: '7px', position: 'absolute', bottom: '13px', left: '16px' }}>
                        <FontAwesomeIcon icon={faCircle} />
                      </span>
                    )}
                  </Link>
                ) : baseName === 'rc_calling_recording' ? (
                  <DownloadRcRecordingButton id={row.id} />
                ) : baseName === 'non_fundable_state_change' ? (
                  <Link
                    key={`view-${row.id}`}
                    to={`/customer/${row.id}`}
                    className="button is-primary"
                  >
                    <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                  </Link>
                ) : baseName === 'payment' && row.module_code_key === "wire_approval_queue" ? (
                  <Link
                    key={`view-${row.id}`}
                    to={`/payment_schedule/${row.customer_id}`}
                    className="button is-primary"
                  >
                    <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                  </Link>
                ) : (baseName === 'payment_check_batch' && !isSubpanel) ? (
                  <table style={{ border: 'none' }}>
                    <tbody>
                      <tr>
                        <td>
                          <Link
                            key={`view-${row.id}`}
                            to={getRoleBasedRouting(active, dropdownOptions, baseName, row, isSubpanel, subBaseName)}
                            className="button is-primary"
                          >
                            <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                          </Link>
                        </td>
                        <td>
                          <Link
                            key={`edit-${row.id}`}
                            to={`/bulk_check_entry/${row.id}`}
                            className="button is-info"
                            onClick={(e) => {
                              if (row.processing_status_dropdown_option_id !== processingStatusVerifyDDOId) {
                                e.preventDefault(); // Prevents navigation
                              }
                            }}
                            disabled={row.processing_status_dropdown_option_id !== processingStatusVerifyDDOId}
                          >
                            <IconWithHelperText icon={faPencil} helperText={'Edit Record'} />
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : baseName === 'loans_requiring_attention' ? (
                  <Link
                    key={`view-${row.id}`}
                    to={`/payment_schedule/${row.customer_id}?loan=${row.guid}`}
                    className="button is-primary"
                  >
                    <IconWithHelperText icon={faBinoculars} helperText={'Payment Schedule'} />
                  </Link>
                ) : baseName !== 'notification' && baseName !== 'manager_approval' && baseName !== 'rc_calling_recording' ? (
                  <Link
                    key={`view-${row.id}`}
                    to={getRoleBasedRouting(active, dropdownOptions, baseName, row, isSubpanel, subBaseName)}
                    className="button is-primary"
                  >
                    <IconWithHelperText icon={faBinoculars} helperText={'View Record'} />
                  </Link>
                ) : null}
              </th>
            ) : (
              ''
            )}
            {baseName === 'payment' && row.module_code_key === "wire_approval_queue" && (<WireApprovalButtons loanId={`${row?.loan_id}`} customerId={`${row?.customer_id}`} id={`${row?.id}`} loanNumber={`${row?.guid}`} rowStyle={rowStyle} />)}
            {cols}
            {hasActions ? (
              <td className={`${rowStyle}`}>
                <div className="field is-grouped" style={{ justifyContent: (baseName === 'payment_check_entry' && isSubpanel) ? "center" : "flex-end" }}>
                  {/* display manager approval icon if there is mgr_approval_status_dropdown_option_id */}
                  {baseName === "payment_schedule" && row?.mgr_approval_status_dropdown_option_id && (
                    <div className="dropdown is-up is-right control">
                      <ManagerApprovalIcons
                        type={dropdownOptions.find((x) => x?.id === row?.mgr_approval_status_dropdown_option_id)?.code_key}
                        tooltipDirection="left"
                        row={row}
                        dropdownOptions={dropdownOptions}
                      />
                    </div>
                  )}
                  {baseName === 'payment_check_entry' && isSubpanel && (
                    !row?.payment_id ? (
                      <span
                        className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-left has-tooltip-primary has-text-weight-normal'}
                        data-tooltip={`${row?.payment_check_batch?.processing_status_dropdown_option_id == processingStatusVerifyDDOId ? 'Cannot assign payments because the batch is not finalized' : 'Assign to Payment'}`}
                      >
                        <button
                          type="button"
                          key={`assign-${row.id}`}
                          className="button is-primary button-payment-check-entry"
                          onClick={() => handlePaymentEntryAssignmentClick(row)}
                          disabled={row?.payment_id || row?.payment_check_batch?.processing_status_dropdown_option_id == processingStatusVerifyDDOId}
                        >
                          <FontAwesomeIcon icon={faArrowRightFromBracket} />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        key={`assign-${row.id}`}
                        className="button is-primary button-payment-check-entry"
                        onClick={() => handlePaymentEntryAssignmentClick(row)}
                        disabled={row?.payment_id || row?.payment_check_batch?.processing_status_dropdown_option_id == processingStatusVerifyDDOId}
                      >
                        <FontAwesomeIcon icon={faArrowRightFromBracket} />
                      </button>
                    )
                  )}
                  {baseName === "payment_schedule" && (
                    <PaymentScheduleOptions
                      baseName={baseName}
                      row={row}
                      updatePaymentStatus={updatePaymentStatus}
                      discountPayment={discountPayment}
                      undiscountPayment={undiscountPayment}
                      undeferPayment={undeferPayment}
                      active={active}
                      nextRegularPaymentId={nextRegularPaymentId}
                      promotionRedeem={promotionRedeem}
                      loanId={loanId}
                      refreshPaymentSchedule={refreshPaymentSchedule}
                      customerLocked={customerLocked}
                      recordChargeback={recordChargeback}
                      submitUnlock={submitUnlock}
                      revertReschedule={revertReschedule}
                    />
                  )}
                  {hasEdit && baseName !== 'payment_schedule' && (
                    <p className="control">
                      <Link
                        key={`edit-${row.id}`}
                        to={`/${baseName}/form/${row.id}`}
                        className="button is-info"
                      >
                        <IconWithHelperText icon={faPencil} helperText={'Edit Record'} />
                      </Link>
                    </p>
                  )}
                  {(hasDelete || (row?.uploaded_by_user_id == active.data.id && isDateAfterDays(row?.created_at, 30))) && (
                    <p className="control">
                      {baseName === 'payment_schedule' ? (
                        row?.can_revert_defer ? (
                          <button
                            type="button"
                            key={`revert-defer-${row.id}`}
                            className="button is-danger"
                            onClick={() => { revertDeferr(row.id); }}
                          >
                            <IconWithHelperText icon={faUndo} helperText={'Undo'} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            key={`delete-${row.id}`}
                            className={row?.can_delete === false && row?.can_delete !== undefined ? 'button has-background-grey has-text-white' : 'button is-danger'}
                            disabled={row?.can_delete === false && row?.can_delete !== undefined}
                            onClick={() => { deletePayment(row.id); }}
                          >
                            <IconWithHelperText icon={faTrash} helperText={'Delete Record'} />
                          </button>
                        )
                      ) : baseName === 'document' && isSubpanel ? (
                        dropdownOptions.find((x) => x?.id === row?.document_type_dropdown_option_id)?.code_key == 'document_type_loan_agreement' && !hasUserPermission(activePermissions, 'is_dev') ? '' : (
                          <button
                            type="button"
                            key={`delete-${row.id}`}
                            className="button is-danger"
                            onClick={() => {
                              dispatch(setModal({ is_active: true, type: 'delete_record', options: { baseName: subBaseName, subBaseName: subBaseName, id: row.id } }));
                            }}
                          >
                            <IconWithHelperText icon={faTrash} helperText={'Delete Record'} />
                          </button>
                        )
                      ) : baseName !== 'payment_schedule' && isSubpanel == true && isMappingTable == true ? (
                        <button
                          type="button"
                          key={`delete-${row.id}`}
                          className="button is-danger"
                          onClick={() => {
                            dispatch(setModal({ is_active: true, type: 'delete_record', options: { baseName: baseName, subBaseName: subBaseName, id: row.id, isMappingTable: true, link: baseName + '/' + parentModuleId + '/' + subBaseName + '/' + row.id } }));
                          }}
                        >
                          <IconWithHelperText icon={faTrash} helperText={'Delete Record'} />
                        </button>
                      ) : baseName !== 'payment_schedule' && isSubpanel == true && isMappingTable == false ? (
                        <button
                          type="button"
                          key={`delete-${row.id}`}
                          className="button is-danger"
                          onClick={() => {
                            dispatch(setModal({ is_active: true, type: 'delete_record', options: { baseName: subBaseName, subBaseName: subBaseName, id: row.id } }));
                          }}
                        >
                          <IconWithHelperText icon={faTrash} helperText={'Delete Record'} />
                        </button>
                      ) : baseName !== 'payment_schedule' && isSubpanel == false ? (
                        <button
                          type="button"
                          key={`delete-${row.id}`}
                          className="button is-danger"
                          onClick={() => {
                            dispatch(setModal({ is_active: true, type: 'delete_record', options: { baseName: baseName, id: row.id } }));
                          }}
                        >
                          <IconWithHelperText icon={faTrash} helperText={'Delete Record'} />
                        </button>
                      ) : null}
                    </p>
                  )}
                </div>
              </td>
            ) : (
              ''
            )}
          </tr>
          {customRowComponent}
        </React.Fragment>
      )
    })

    return dropdowns.status === 'succeeded' ? tableRow : ''
  } else {
    return ''
  }
}
